import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const numericId = (value) => String(value ?? '').split('/').pop();

/** A single owner updates the visual composition and the compact text summary. */
class PelucheKitHero extends Component {
  #controller;
  #revision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.handleSelection, {
      signal: this.#controller.signal,
    });
    this.bindImages();
    const composition = this.querySelector('[data-kit-composition]');
    if (!composition?.dataset.roles || composition.dataset.componentCount !== composition.dataset.expectedCount) {
      this.showFallback();
    }
  }

  disconnectedCallback() {
    ++this.#revision;
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  showFallback() {
    const native = this.closest('[data-kit-visual]')?.querySelector('[data-kit-native-fallback]');
    this.hidden = true;
    if (native instanceof HTMLElement) native.hidden = false;
    const summary = document.getElementById(this.dataset.summaryId);
    if (summary) summary.hidden = true;
    this.setStatus(this.dataset.unresolved);
  }

  setStatus(text) {
    const status = this.querySelector('[data-kit-status]');
    if (status) status.textContent = text ?? '';
  }

  bindImages() {
    for (const image of this.querySelectorAll('[data-kit-composition] img')) {
      image.dataset.loadState = 'loading';
      const loaded = () => { image.dataset.loadState = 'loaded'; };
      const missing = () => {
        if (!image.isConnected || !this.contains(image)) return;
        image.dataset.loadState = 'error';
        this.showFallback();
      };
      image.addEventListener('error', missing, { once: true, signal: this.#controller.signal });
      image.addEventListener('load', loaded, { once: true, signal: this.#controller.signal });
      if (image.complete && image.naturalWidth === 0) missing();
      else if (image.complete) loaded();
    }
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  handleSelection = async (event) => {
    if (numericId(event.product?.id) !== this.dataset.productId) return;
    if (event.target instanceof Element &&
        event.target.closest('[data-product-grid-content]') !== this.closest('[data-product-grid-content]')) return;
    const revision = ++this.#revision;
    const old = this.querySelector('[data-kit-composition]');
    const summary = document.getElementById(this.dataset.summaryId);
    if (!(old instanceof HTMLElement)) return;
    old.hidden = true;
    if (summary) summary.hidden = true;
    this.setAttribute('aria-busy', 'true');
    this.setStatus(this.dataset.loading);
    const selection = new Map(event.selectedOptions?.map(({ name, value }) => [name, value]));

    try {
      // The native picker owns the only variant request. Reuse its parsed response.
      const result = await event.promise;
      if (revision !== this.#revision || !this.isConnected) return;
      const html = result.detail?.html;
      const next = html?.getElementById(this.id)?.querySelector('[data-kit-composition]');
      const nextSummary = html?.getElementById(this.dataset.summaryId);
      const resourceId = numericId(result.detail?.resource?.id);
      const variantId = numericId(result.variant?.id);
      if (!next || !nextSummary || !summary || !resourceId || !variantId ||
          numericId(result.detail?.productId) !== this.dataset.productId ||
          next.dataset.productId !== this.dataset.productId ||
          nextSummary.dataset.productId !== this.dataset.productId ||
          next.dataset.variantId !== resourceId || resourceId !== variantId ||
          nextSummary.dataset.variantId !== resourceId ||
          next.dataset.roles !== old.dataset.roles || nextSummary.dataset.roles !== old.dataset.roles ||
          next.dataset.expectedCount !== old.dataset.expectedCount ||
          next.dataset.componentCount !== old.dataset.expectedCount ||
          nextSummary.dataset.componentCount !== old.dataset.expectedCount ||
          next.dataset.line !== (selection.get('Linha do banho') ?? '') ||
          next.dataset.scent !== (selection.get('Fragrância') ?? '')) {
        throw new Error('Unresolved pilot composition');
      }
      const roles = old.dataset.roles.split('|');
      const items = [...next.querySelectorAll('[data-component-id]')];
      const texts = [...nextSummary.querySelectorAll('[data-component-id]')];
      if (items.length !== roles.length || texts.length !== roles.length || items.some((item, index) =>
          item.dataset.role !== roles[index] || !item.dataset.componentId ||
          ['componentId', 'sku', 'role', 'name', 'volume', 'line', 'scent', 'imageUrl'].some((key) =>
            !item.dataset.componentId || item.dataset[key] !== texts[index]?.dataset[key]))) {
        throw new Error('Incomplete pilot composition');
      }

      const lineIds = { 'Blueberry': ['9836127781094', '9836113101030', '9836120998118'], 'Pitanga Negra': ['9836128174310', '9836113199334', '9836121063654'] };
      const scentIds = {"Baby": "9836123422950", "Passion": "9836124405990", "Blueberry": "9836123848934", "Pitanga Negra": "9836125094118", "Lemone": "9837684719846", "Serena": "9837685113062", "Orion": "9837685080294", "Luxor": "9837684818150", "Lumina": "9837684916454", "Avelle": "9837684949222", "Cedrus": "9837684981990"};
      const expectedRoles = {
        '15399451164902': ['shampoo','condicionador'],
        '15399499301094': ['shampoo','condicionador','mascara'],
        '15399514013926': ['shampoo','condicionador','perfume'],
        '15399535739110': ['shampoo','condicionador','mascara','perfume'],
        '15399537574118': ['damasco','perfume'],
      }[this.dataset.productId];
      if (!expectedRoles || expectedRoles.join('|') !== roles.join('|') || items.some((item) => {
        const role = item.dataset.role;
        const expected = role === 'damasco' ? '9836113985766' : role === 'perfume' ? scentIds[next.dataset.scent] : lineIds[next.dataset.line]?.[['shampoo','condicionador','mascara'].indexOf(role)];
        return !expected || item.dataset.componentId !== expected;
      })) throw new Error('Invalid component identity');

      const composition = next.cloneNode(true);
      const description = nextSummary.cloneNode(true);
      composition.hidden = false;
      description.hidden = false;
      old.replaceWith(composition);
      summary.replaceWith(description);
      this.hidden = false;
      const native = this.closest('[data-kit-visual]')?.querySelector('[data-kit-native-fallback]');
      if (native instanceof HTMLElement) native.hidden = true;
      this.bindImages();
      this.removeAttribute('aria-busy');
      this.setStatus(`${this.dataset.updated} ${[next.dataset.line, next.dataset.scent].filter(Boolean).join(' / ')}.`);
    } catch {
      if (revision !== this.#revision || !this.isConnected) return;
      this.removeAttribute('aria-busy');
      this.showFallback();
      // The native gallery remains available; price and CTA keep their native owners.
    }
  };
}

if (!customElements.get('peluche-kit-hero')) customElements.define('peluche-kit-hero', PelucheKitHero);
